import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";
import type { TaskTemplate, TaskTemplatePriorityEnum } from "@/api/generated/client/models/TaskTemplate";

interface TaskDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    task?: TaskTemplate | null;
    mode: 'create' | 'edit';
    onSave: (task: Partial<TaskTemplate>) => void;
    isLoading?: boolean;
}

export function TaskDialog({
    open,
    onOpenChange,
    task,
    mode,
    onSave,
    isLoading
}: TaskDialogProps) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState<TaskTemplatePriorityEnum>("medium");
    const [dueDaysOffset, setDueDaysOffset] = useState<number | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setError(null);
            if (mode === 'edit' && task) {
                setTitle(task.title);
                setDescription(task.description || "");
                setPriority(task.priority);
                setDueDaysOffset(task.dueDaysOffset || undefined);
            } else {
                setTitle("");
                setDescription("");
                setPriority("medium");
                setDueDaysOffset(undefined);
            }
        }
    }, [open, mode, task]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (dueDaysOffset !== undefined && dueDaysOffset !== null) {
            if (dueDaysOffset > 365) {
                setError("Due days must be less than 365");
                return;
            }
            if (dueDaysOffset < 0) {
                setError("Due days cannot be negative");
                return;
            }
        }

        onSave({
            title,
            description: description || null,
            priority,
            dueDaysOffset: dueDaysOffset ?? null,
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{mode === 'create' ? 'Create New Task' : 'Edit Task'}</DialogTitle>
                    <DialogDescription>
                        {mode === 'create'
                            ? 'Add a new task to this status workflow.'
                            : 'Modify the details of this task.'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    {error && (
                        <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
                            {error}
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="task-title">Task Title *</Label>
                        <Input
                            id="task-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., Review Policy Documents"
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="task-description">Description</Label>
                        <Input
                            id="task-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional description"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="task-priority">Priority</Label>
                            <Select
                                value={priority}
                                onValueChange={(value) => setPriority(value as TaskTemplatePriorityEnum)}
                            >
                                <SelectTrigger id="task-priority">
                                    <SelectValue placeholder="Select priority" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="task-due">Due in (days)</Label>
                            <Input
                                id="task-due"
                                type="number"
                                min="0"
                                value={dueDaysOffset || ""}
                                onChange={(e) => setDueDaysOffset(e.target.value ? parseInt(e.target.value) : undefined)}
                                placeholder="Optional"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <span className="mr-2 animate-spin">⏳</span>}
                            <Save className="h-4 w-4 mr-2" />
                            Save Task
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
